import {
  AfterViewInit,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Post, User } from 'src/app/core/models/core.models';
import { selectPostById, selectUser } from 'src/app/core/reducers';

import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/reducers';
import { NomSentiment } from 'src/app/core/constants';
import { PostFullContentComponent } from '../../post/post-full-content/post-full-content.component';
import { PostSourceInfoComponent } from '../../post/post-source-info/post-source-info.component';
import { PostSynopsisComponent } from '../../post/post-synopsis/post-synopsis.component';
import { PostTranslationComponent } from '../../post/post-translation/post-translation.component';
import { PropertyViewConfig } from 'src/app/core/services/feed-settings.service';
import { Store } from '@ngrx/store';
import { StoryPostsComponent } from './story-posts/story-posts.component';
import { StoryRunParamsComponent } from './story-run-params/story-run-params.component';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { loadPost } from 'src/app/core/actions/post.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toggleFeedView } from 'src/app/core/actions/feed.actions';

@Component({
  selector: 'app-story-card-details',
  templateUrl: './story-card-details.component.html',
  styleUrls: ['./story-card-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StoryCardDetailsComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() postId: string;
  @Input() feedId: string;
  @Input() viewConfig: PropertyViewConfig[];

  post$: Observable<Post>;
  post: Post;
  user: User;

  currentComponent = null;
  @ViewChild('tabContent', { read: ViewContainerRef })
  tabContent: ViewContainerRef;
  NomSentiment = NomSentiment;
  isContentExpanded = false;
  /** Public post-view URL for the title link (Full Content's "Internal URL"). */
  postUrlInternal: string;

  tabs: Array<any>;

  private destroyRef = inject(DestroyRef);

  constructor(
    protected store: Store<AppState>,
    private translateService: TranslateService,
  ) {
    this.store
      .select(selectUser)
      .pipe(
        tap((user) => {
          this.user = user;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postId'] && this.postId) {
      this.isContentExpanded = false;
      this.postUrlInternal =
        environment.baseUrl + '/post/view/?post_id=' + this.postId;
      this.store.dispatch(
        loadPost({ postId: this.postId, feedId: this.feedId }),
      );
      this.post$ = this.store.select(selectPostById(this.postId));
      this.post$
        .pipe(
          tap((post) => {
            this.post = post;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    const updatedInputs: any = {};
    if (changes['postId']) updatedInputs.postId = this.postId;
    if (changes['feedId']) updatedInputs.feedId = this.feedId;
    if (changes['viewConfig']) updatedInputs.viewConfig = this.viewConfig;

    if (this.tabs && Object.keys(updatedInputs).length > 0) {
      this.tabs.forEach((tab) => {
        for (const key of Object.keys(updatedInputs)) {
          if (key in tab.inputs) {
            tab.inputs[key] = updatedInputs[key];
          }
        }
      });
      this.updateTabsCachedComponentsInputs(updatedInputs);
    }
  }

  ngOnInit(): void {
    this.setupTabs();
  }

  private setupTabs() {
    this.tabs = [
      {
        label: this.translateService.instant(
          'story-card-details.tab.story-posts',
        ),
        active: true,
        component: StoryPostsComponent,
        inputs: {
          postId: this.postId,
          feedId: this.feedId,
          viewConfig: this.viewConfig,
        },
        visible: true,
      },
      {
        label: this.translateService.instant(
          'story-card-details.tab.run-params',
        ),
        active: false,
        component: StoryRunParamsComponent,
        inputs: {
          postId: this.postId,
        },
        visible: true,
      },
      {
        label: this.translateService.instant(
          'post-card-details.label.full-content',
        ),
        active: false,
        component: PostFullContentComponent,
        inputs: {
          postId: this.postId,
        },
        visible:
          this.user.feature_components[
            AppFeatures.POST_DETAILS_PANE_FULL_CONTENT
          ],
      },
      // {
      //   label: this.translateService.instant('post-card-details.label.video'),
      //   component: PostVideoComponent,
      //   inputs: {
      //     postId: this.postId,
      //   },
      //   visible: true,
      // },
      {
        label: this.translateService.instant('post-card-details.label.source'),
        component: PostSourceInfoComponent,
        inputs: {
          postId: this.postId,
        },
        visible: false,
      },
      {
        label: this.translateService.instant(
          'post-card-details.label.synopsis',
        ),
        component: PostSynopsisComponent,
        inputs: {
          postId: this.postId,
        },
        visible:
          this.user.feature_components[AppFeatures.POST_DETAILS_PANE_SYNOPSIS],
      },
      {
        label: this.translateService.instant(
          'post-card-details.label.translation',
        ),
        component: PostTranslationComponent,
        inputs: {
          postId: this.postId,
        },
        visible:
          this.user.feature_components[
            AppFeatures.POST_DETAILS_PANE_TRANSLATION
          ],
      },
      // {
      //   label: this.translateService.instant(
      //     'post-card-details.label.quick-story-report',
      //   ),
      //   component: PostQuickStoryComponent,
      //   inputs: {
      //     postId: this.postId,
      //   },
      //   visible:
      //     this.user.feature_components[
      //       AppFeatures.POST_DETAILS_PANE_QUICK_STORY_REPORT
      //     ],
      // },
      // {
      //   label: this.translateService.instant(
      //     'post-card-details.label.image-tagging',
      //   ),
      //   component: PostImageTaggingComponent,
      //   inputs: {
      //     postId: this.postId,
      //   },
      //   visible:
      //     Object.prototype.hasOwnProperty.call(
      //       this.user.feature_components,
      //       AppFeatures.FEED_SEARCH_RESULTS_ARTICLE_IMAGE_FACE_DETECTION,
      //     ),
      // },
    ];

    this.tabs.forEach((tab, index) => {
      if (tab.active) {
        this.selectTab(tab, index);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.tabs) {
      this.tabs.forEach((tab, index) => {
        if (tab.active) {
          this.selectTab(tab, index);
        }
      });
    }
  }

  loadComponent(component, inputs: any, index: number): void {
    const viewContainerRef = this.tabContent;
    const existingTab = this.tabs[index];

    viewContainerRef?.detach();

    if (!existingTab.componentRef) {
      const componentRef = viewContainerRef?.createComponent(component);
      if (componentRef) {
        const instance = componentRef.instance as any;
        if (inputs) {
          const changes: SimpleChanges = {};
          for (let key of Object.keys(inputs)) {
            instance[key] = inputs[key];
            changes[key] = new SimpleChange(undefined, inputs[key], true);
          }
          if (Object.keys(changes).length > 0 && instance.ngOnChanges) {
            instance.ngOnChanges(changes);
          }
        }
        existingTab.componentRef = componentRef;
      }
    } else {
      const componentRef = existingTab.componentRef;
      if (inputs) {
        const changes: SimpleChanges = {};
        for (let key of Object.keys(inputs)) {
          const previousValue = componentRef.instance[key];
          componentRef.instance[key] = inputs[key];
          changes[key] = new SimpleChange(
            previousValue,
            inputs[key],
            previousValue === undefined,
          );
        }
        if (
          Object.keys(changes).length > 0 &&
          componentRef.instance.ngOnChanges
        ) {
          componentRef.instance.ngOnChanges(changes);
        }
      }
    }

    viewContainerRef?.insert(existingTab.componentRef.hostView);
    this.currentComponent = existingTab.componentRef;
  }

  updateTabsCachedComponentsInputs(inputs: any) {
    if (this.tabs) {
      this.tabs.forEach((tab) => {
        if (tab.componentRef) {
          const componentRef = tab.componentRef;
          const changes: SimpleChanges = {};

          for (let key of Object.keys(inputs)) {
            const currentValue = inputs[key];
            const previousValue = componentRef.instance[key];
            componentRef.instance[key] = currentValue;

            changes[key] = new SimpleChange(
              previousValue,
              currentValue,
              previousValue === undefined,
            );
          }

          if (
            Object.keys(changes).length > 0 &&
            componentRef.instance.ngOnChanges
          ) {
            componentRef.instance.ngOnChanges(changes);
          }
        }
      });
    }
  }

  selectTab(tab, index): void {
    this.tabs.forEach((t) => (t.active = false));
    tab.active = true;
    this.loadComponent(tab.component, tab.inputs, index);
  }

  closeDetails() {
    this.store.dispatch(
      toggleFeedView({
        feedId: this.feedId,
        view: 'details',
        selected: false,
      }),
    );

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }
}
